/**
 * Functions to render the proper request component based on
 * user treatments
 */

import React from 'react'
import { Redirect } from 'react-router-dom'
import store from './stores/configureStore'
import { showError, selectContact, setIsBillSplit } from './actions/Actions'
import {
  parseAndFormatPhoneNumber,
  isPhoneNumberMaybe,
} from './utils/phoneUtils'
import { parseSearch } from './utils/url-utils'
import linksURL from '../../../config/links.json'

import content from 'pp-react-l10n'
import { ENV_TYPE } from '../../../lib/constants'
const i18n = content('transfer/page/recipient')

// Called by `onEnter` to guard entry to /split route based on pxp
export function splitOnEnter() {
  const {
    server: {
      isEnrolledInBillSplitting,
      rootURL,
      env,
      businessAccountInfo: { isBusinessUser } = {},
    } = {},
  } = store.getState()

  // bill split doesnt apply to ppcp route - its for business users to request money from one user
  if (env === ENV_TYPE.HAWK_PPCP) {
    return
  }

  /**
   * Merchants are prohibited from completing Bill Split transactions.
   * This flow will be enabled once services are updated. This code block
   * is meant to give the merchants an indicators of why they cannot see
   * the flow via and error overpanel. The error panel state needs to move
   * from false to true and wrapping the dispatch in a setTimeout
   * accomplishes this.
   */
  const requestReviewEnv = [ENV_TYPE.MPP, ENV_TYPE.PROFILE]

  if (isBusinessUser && !requestReviewEnv.includes(env)) {
    setTimeout(
      () =>
        store.dispatch(
          showError({
            message: i18n('error.merchantSplitError', {
              accountCreationLink: linksURL.ALL.ACCOUNT_SELECTION_URL,
            }),
          })
        ),
      0
    )
  }

  if (requestReviewEnv.includes(env)) {
    return redirectToRequestPreview()
  }

  if (!isEnrolledInBillSplitting || isBusinessUser) {
    return <Redirect to={`${rootURL}/request`} />
  }

  /**
   * Allow Bill Split flow with isBillSplit query param.
   * Added because Flow Consolidation doesn't know when is Bill Split flow since we don't have recipients
   */
  const params = parseSearch(location.search)
  const isBillSplit = Boolean(params.isBillSplit)
  if (isBillSplit) {
    store.dispatch(setIsBillSplit(true))
  }
}

// Called by `onEnter` to guard entry to /split route based on pxp
export function redirectToRequestPreview() {
  const {
    server: { env, rootURL, country } = {},
    recipient: { payerId, isRecipientValid, recipient, recipientId },
    external: { isBillSplit } = {},
  } = store.getState()

  if (env === ENV_TYPE.PROFILE) {
    if (payerId) {
      return <Redirect to={`${rootURL}/request/preview`} />
    }
    return <Redirect to={`${rootURL}/request`} />
  }
  if (env === ENV_TYPE.PISP) {
    // Add params so we can handle refresh from node layer
    let params = window.location.search || ''
    if (params) {
      params += '&env=pisp'
    }
    return <Redirect to={`${rootURL}/request/preview${params}`} />
  }
  if (isRecipientValid && (recipient || recipientId)) {
    const displayName = isPhoneNumberMaybe(recipient)
      ? parseAndFormatPhoneNumber(recipient, country)
      : ''

    store.dispatch(selectContact({ email: recipient, displayName, index: -1 }))
    return <Redirect to={`${rootURL}/request/preview`} />
  }
  if (isBillSplit) {
    return <Redirect to={`${rootURL}/request/preview`} />
  }
}

// Allow a easy path to preview page for bill splitting
export function redirectToSplitPreview() {
  const {
    server: { rootURL } = {},
    amount: { amount, currencyCode } = {},
  } = store.getState()

  if (amount && currencyCode) {
    return <Redirect to={`${rootURL}/split/preview`} />
  }
}

// Prevent users from hitting success page directly
export function redirectToHomepage() {
  const {
    server: { rootURL } = {},
    multiRecipient: { recipients },
  } = store.getState()

  if (recipients.length === 0) {
    return <Redirect to={rootURL} />
  }
}
